import type { UseMutationReturnType, UseQueryReturnType } from '@tanstack/vue-query';
import { useQuery, useQueryClient } from '@tanstack/vue-query';
import { createFavorite, deleteFavorite, getAllFavorites } from '../../api-v2/web/favorites/endpoints';
import type { Ref } from 'vue';
import { computed } from 'vue';
import type { VQQueryOptions } from '@/types/VueQueryTypes';
import type { VQMutationOptions } from '@/composables/queries/defineMutation';
import { useManagedMutation } from '@/composables/queries/defineMutation';
import type {
  FavoriteCreateRequestDTO,
  FavoriteDTO,
  FavoriteListResponseDTO,
  PortfolioFavoriteDTO,
  StrategyFavoriteDTO,
} from '@/api-v2/web/favorites';
import { useToasts } from '../useToasts';
import useTranslation from '../useTranslation';
import { limitedQueryFn } from './useLimitedQueries';

export const favoriteKeys = {
  all: () => [{ scope: 'favorites' }] as const,
  list: () => [{ ...favoriteKeys.all()[0], entity: 'list' }] as const,
  favorite: (favoriteId: Ref<string>) => [{ ...favoriteKeys.all()[0], entity: 'favorite', favoriteId }] as const,
};

export const favoritesSelect = {
  mapIdToFavoriteId: (data: FavoriteListResponseDTO): Map<string, string> => {
    const map = new Map<string, string>();

    for (const favorite of data.portfolio ?? []) {
      map.set(favorite.portfolioId, favorite.id);
    }

    for (const favorite of data.strategy ?? []) {
      map.set(favorite.timeSeriesEntityId, favorite.id);
    }

    return map;
  },
};

export function useGetAllFavorites(
  options: VQQueryOptions<FavoriteListResponseDTO> = {},
): UseQueryReturnType<FavoriteListResponseDTO, Error> {
  return useQuery<FavoriteListResponseDTO, Error>({
    queryKey: favoriteKeys.list(),
    queryFn: limitedQueryFn(({ signal }): Promise<FavoriteListResponseDTO> => getAllFavorites(signal)),
    ...options,
    refetchOnMount: 'always',
  });
}

export function useCreateFavorite(
  options: VQMutationOptions<FavoriteDTO, Error, FavoriteCreateRequestDTO> = {},
): UseMutationReturnType<FavoriteDTO, Error, FavoriteCreateRequestDTO, unknown> {
  const queryClient = useQueryClient();
  const { errorToast } = useToasts();
  const { translate } = useTranslation();

  return useManagedMutation(
    {
      mutationFn: (data: FavoriteCreateRequestDTO) => createFavorite(data),
      invalidateCache() {
        return new Promise<void>((resolve) => {
          resolve();
        });
      },
      onSuccess(newCacheEntry) {
        const isPortfolioFavorite = 'portfolioId' in newCacheEntry;

        queryClient.setQueryData(
          favoriteKeys.list(),
          (oldData: FavoriteListResponseDTO | undefined): FavoriteListResponseDTO => {
            if (!oldData) {
              return isPortfolioFavorite
                ? { portfolio: [newCacheEntry as PortfolioFavoriteDTO], strategy: [] }
                : { portfolio: [], strategy: [newCacheEntry as StrategyFavoriteDTO] };
            }

            if (isPortfolioFavorite) {
              return {
                ...oldData,
                portfolio: [...(oldData.portfolio ?? []), newCacheEntry as PortfolioFavoriteDTO],
              };
            }

            return {
              ...oldData,
              strategy: [...(oldData.strategy ?? []), newCacheEntry as StrategyFavoriteDTO],
            };
          },
        );
      },
      onError(error) {
        errorToast(
          `${translate({
            path: 'TOASTS.FAVORITE.CREATE.FAILED',
          })}: ${error}`,
        );
      },
      mutationKey: ['create-favorite'],
    },
    options,
  );
}

export function useDeleteFavorite(
  options: VQMutationOptions<FavoriteDTO, Error, string> = {},
): UseMutationReturnType<FavoriteDTO, Error, string, unknown> {
  const queryClient = useQueryClient();
  const { errorToast } = useToasts();
  const { translate } = useTranslation();

  return useManagedMutation(
    {
      mutationFn: deleteFavorite,
      invalidateCache() {
        return new Promise<void>((resolve) => {
          resolve();
        });
      },
      onSuccess(oldFavorite) {
        queryClient.setQueryData(favoriteKeys.list(), (oldData: FavoriteListResponseDTO | undefined) => {
          if (!oldData) return;

          return {
            portfolio: oldData.portfolio?.filter((favorite) => favorite.id !== oldFavorite.id),
            strategy: oldData.strategy?.filter((favorite) => favorite.id !== oldFavorite.id),
          };
        });
      },
      onError(error) {
        errorToast(
          `${translate({
            path: 'TOASTS.FAVORITE.DELETE.FAILED',
          })}: ${error}`,
        );
      },
      mutationKey: ['delete-favorite'],
    },
    options,
  );
}

export function useGetToggleFavoriteFn(): (favoriteEntity: FavoriteCreateRequestDTO) => Promise<FavoriteDTO> {
  const favorites = useGetAllFavorites();
  const favoriteStrategies = computed((): StrategyFavoriteDTO[] => favorites.data.value?.strategy ?? []);
  const favoritePortfolios = computed((): PortfolioFavoriteDTO[] => favorites.data.value?.portfolio ?? []);

  const createMutation = useCreateFavorite();
  const deleteMutation = useDeleteFavorite();

  const getServerSideFavorite = (
    favoriteEntity: FavoriteCreateRequestDTO,
  ): StrategyFavoriteDTO | PortfolioFavoriteDTO | undefined => {
    if ('portfolioId' in favoriteEntity) {
      return favoritePortfolios.value.find((p) => p.portfolioId === favoriteEntity.portfolioId);
    }

    return favoriteStrategies.value.find((s) => s.timeSeriesEntityId === favoriteEntity.timeSeriesEntityId);
  };

  return (favoriteEntity: FavoriteCreateRequestDTO): Promise<FavoriteDTO> => {
    const serverSideFavorite = getServerSideFavorite(favoriteEntity);

    if (!serverSideFavorite) {
      return createMutation.mutateAsync(favoriteEntity);
    }

    return deleteMutation.mutateAsync(serverSideFavorite.id);
  };
}
